/**
 * Normalize an arbitrary string into a URL-safe slug matching the regex
 * `^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$` used by the reseller API (and
 * other slugged entities).
 *
 * Rules:
 *  - Lowercase
 *  - Strip diacritics ("café" → "cafe", "מָה" → "מ")
 *  - Replace any run of non-`[a-z0-9]` chars with a single `-`
 *  - Trim leading and trailing `-`
 *  - Cap at 64 characters
 *  - Single-char inputs survive ("x" → "x"); the single-char form is
 *    consistent with the regex (the `{0,62}` middle is optional)
 *
 * Returns `''` when the input has no representable alphanumeric
 * content (e.g. all punctuation or a non-Latin script with no Latin
 * fall-back after stripping diacritics).
 *
 * This helper is intentionally pure and dependency-free so it can run
 * on every keystroke without surprise costs.
 */
export function slugify(input: string): string {
  if (!input) return '';
  // Decompose accented characters then drop the combining marks.
  // U+0300..U+036F is the "Combining Diacritical Marks" block.
  const stripped = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

  // Replace any non-alphanumeric run with a single hyphen.
  const collapsed = stripped.replace(/[^a-z0-9]+/g, '-');

  // Trim leading/trailing hyphens then cap at 64 chars. Trimming after
  // cap is required so a 65th char that's a hyphen doesn't leave a
  // dangling trailing hyphen on the slice.
  const trimmed = collapsed.replace(/^-+|-+$/g, '');
  if (trimmed.length <= 64) return trimmed;
  return trimmed.slice(0, 64).replace(/-+$/g, '');
}
